export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी' },
  { code: 'es', name: 'Spanish', native: 'Español' },
  { code: 'fr', name: 'French', native: 'Français' },
  { code: 'de', name: 'German', native: 'Deutsch' },
  { code: 'zh', name: 'Chinese', native: '中文' },
  { code: 'ja', name: 'Japanese', native: '日本語' },
  { code: 'pt', name: 'Portuguese', native: 'Português' },
  { code: 'ru', name: 'Russian', native: 'Русский' },
  { code: 'ar', name: 'Arabic', native: 'العربية' },
  // Indian Regional Languages
  { code: 'te', name: 'Telugu', native: 'తెలుగు' },
  { code: 'ta', name: 'Tamil', native: 'தமிழ்' },
  { code: 'mr', name: 'Marathi', native: 'मराठी' },
  { code: 'bn', name: 'Bengali', native: 'বাংলা' },
  { code: 'gu', name: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'kn', name: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', name: 'Malayalam', native: 'മലയാളം' },
  { code: 'pa', name: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'or', name: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'as', name: 'Assamese', native: 'অসমীয়া' },
  // Add more as needed...
];

/**
 * Safely extracts the appropriate localized string from a JSONB field.
 * 
 * @param {object} jsonbField - The object containing localized strings (e.g. { en: "Hello", hi: "नमस्ते" })
 * @param {string} fallbackText - The legacy non-i18n string (e.g. profile.full_name) to use if jsonb is empty
 * @param {string} visitorLang - The language requested by the visitor (e.g. 'hi')
 * @param {string} creatorPrimaryLang - The creator's default language (defaults to 'en')
 * @returns {string} The best matching string
 */
export const resolveText = (jsonbField, fallbackText, visitorLang, creatorPrimaryLang = 'en') => {
  // If there's no jsonbField at all, just return the standard legacy text
  if (!jsonbField || typeof jsonbField !== 'object' || Object.keys(jsonbField).length === 0) {
    return fallbackText || '';
  }
  
  // 1. Try exact visitor's language (strip regional dialects, e.g., 'en-US' -> 'en')
  const baseVisitorLang = visitorLang?.split('-')[0];
  if (jsonbField[visitorLang]?.trim()) return jsonbField[visitorLang];
  if (jsonbField[baseVisitorLang]?.trim()) return jsonbField[baseVisitorLang];
  
  // 2. Fall back to creator's primary language
  if (jsonbField[creatorPrimaryLang]?.trim()) return jsonbField[creatorPrimaryLang];
  
  // 3. Fall back to English explicitly
  if (jsonbField['en']?.trim()) return jsonbField['en'];
  
  // 4. Fall back to legacy standard text (the original column value before i18n was added)
  if (fallbackText?.trim()) return fallbackText;

  // 5. Absolute last resort: just return whatever the first valid string is in the object
  return Object.values(jsonbField).find(val => typeof val === 'string' && val.trim() !== '') || '';
};

/**
 * Builds a robust font stack appending system fallbacks for global language support.
 * This ensures that if a custom web font lacks glyphs for Hindi, Japanese, etc.,
 * the browser gracefully falls back to the native OS font for that script.
 */
export const getRobustFontStack = (primaryFont) => {
  const base = primaryFont ? `${primaryFont}, ` : '';
  return `${base}system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`;
};
